import React from 'react';
import {
  SalePaymentResponseData,
  useAppContext,
  useTranslator,
  withLoading,
} from '@front-activerse/common-components';
import { useEffect, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { Container, Row, Col } from 'reactstrap';
import { UniversalPlaceholder } from '../../Layout';
import Layout from '../../Layout/Layout';
import HowToActivateInfo from './Components/HowToActivateInfo';
import PostPurchaseInstructions from './Components/PostPurchaseInstructions';
import ReturningUserInfo from './Components/ReturningUserInfo';
import { sha256 } from 'js-sha256';

interface IThankYouContainer {
  payment: SalePaymentResponseData;
  reportPayment: ({ token, operatorData }: { token: string; operatorData?: any }) => Promise<any>;
  token: string;
}

declare global {
  interface Window {
    dataLayer?: any;
  }
}

const ThankYouContainer: React.FC<IThankYouContainer> = ({ payment, reportPayment, token }) => {
  const [userEmail, setUserEmail] = useState();
  const [isPast48Hours, setIsPast48Hours] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const { showBoundary } = useErrorBoundary();
  const { t } = useTranslator();

  const {
    images: { thankYouPageBg, thankYouPageBgMobile },
    consts: { LANDING_URL },
  } = useAppContext();

  if (!payment) {
    window.location.href = String(LANDING_URL);
  }
  const { isCod, containsPhysicalProduct } = payment;

  const handleIsPast48Hours = (created: string) => {
    const now = new Date();
    const userCreatedDate = new Date(created);
    const timeDifference = +now - +userCreatedDate;

    const hoursDifference = timeDifference / (1000 * 60 * 60);
    setIsPast48Hours(hoursDifference >= 48);

    if (isPast48Hours) {
      if (window) {
        window.dataLayer.push({ visitorType: 'hasAccount' });
      }
    }
  };

  const handleReportPayment = async () => {
    const paymentOperatorData =
      global && global.location && global.location.search ? global.location.search : '';

    try {
      const res = await reportPayment({ token, operatorData: paymentOperatorData });
      const result = res.data.sale.reportPayment;
      if (!result) {
        window.location.href = String(LANDING_URL);
        return null;
      }

      if (result.user && result.user.created) {
        handleIsPast48Hours(result.user.created);
      }

      if (result.user && result.user.email) {
        setUserEmail(result.user.email);
      }

      const type = result.__typename;

      if (type === 'NotReportedPayment') {
        if (result.reportData) {
          result.reportData = result.reportData.map(JSON.parse);
          result.reportData.forEach((data: any) => window.dataLayer.push(data));

          const userEmail = global.localStorage.getItem('userEmail') || '';

          window.dataLayer.push({
            event: 'conversion',
            email: userEmail,
            sha256_email: sha256(userEmail),
          });
        }
      } else if (type === 'CanceledPayment') {
        showBoundary('Unexpected response. Typename: CanceledPayment');
      } else if (type === 'ValidationException') {
        showBoundary('ValidationException');
      }
      return null;
    } catch (err) {
      showBoundary(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    handleReportPayment();
  }, []);

  return !isLoading ? (
    <Layout simpleLayout>
      <div className="page-template-buy full-height7">
        <Container className="py-md-4">
          <Row>
            <Col md={{ size: 6, offset: 3 }}>
              <div className="page-container">
                <div className="thankyou-container">
                  <section>
                    <img
                      src={thankYouPageBg}
                      alt=""
                      className="img-fluid d-none d-md-block mb-xl rounded"
                    />
                    <img
                      src={thankYouPageBgMobile}
                      alt=""
                      className="img-fluid d-md-none mb-xl rounded"
                    />
                  </section>
                  {isPast48Hours && !isCod && !containsPhysicalProduct ? (
                    <ReturningUserInfo />
                  ) : (
                    <>
                      <section>
                        <h1 className="title-large semi-bold text-center mb-xl">
                          {t('app.thankUPage.title')}
                        </h1>
                        <p className="text-center">
                          <span className="text-text-variant-color">
                            {t('app.thankUPage.confirmationSentTo')}
                          </span>
                          <br />
                          <strong>{userEmail}</strong>
                        </p>
                      </section>
                      <hr />
                    </>
                  )}

                  {!isPast48Hours && !(isCod || containsPhysicalProduct) && (
                    <PostPurchaseInstructions />
                  )}

                  {(isCod || containsPhysicalProduct) && <HowToActivateInfo />}
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </Layout>
  ) : (
    <UniversalPlaceholder />
  );
};

export default withLoading(ThankYouContainer, UniversalPlaceholder);
